import { Component, ElementRef, HostListener, Input, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { AlertController, ModalController } from '@ionic/angular';
import { GlobalService } from 'src/app/api/global.service';

@Component({
  selector: 'app-details',
  templateUrl: './details.page.html',
  styleUrls: ['./details.page.scss'],
})
export class DetailsPage implements OnInit {

  @Input() step;
  @Input() modalTitle = 'Complete Task';
  step_link: SafeResourceUrl = '';
  @ViewChild('emailFrame') emailFrameRef!: ElementRef<HTMLIFrameElement>;

  // Kept in sync via postMessage from the embedded /email iframe (see email.page.ts,
  // PostDocumentRequestStatusToParent) - true when that page has an uploadable document request
  // that's had files uploaded but hasn't been marked complete yet.
  private hasUnfinishedUpload = false;

  // Kept in sync via postMessage from the embedded /email iframe (see email.page.ts,
  // PostSignatureModalStateToParent) - true while that page has its own signature-request modal
  // open. That inner modal is confined to the iframe's box, so our own header/footer would still
  // show around its edges and let the user close out mid-signature - we hide our chrome instead.
  signingInProgress = false;

  constructor(
    private modalController: ModalController,
    private alertController: AlertController,
    private globalService: GlobalService,
    private sanitizer: DomSanitizer
  ) { }

  ngOnInit() {
    console.log("Step ", this.step);
    if (this.step && this.step.emails && this.step.emails.length > 0) {
      const url = `/email?guid=${this.step.emails[0].guid}&authed=1&skipLoading=1`;
      this.step_link = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    }
  }

  @HostListener('window:message', ['$event'])
  onMessage(event: MessageEvent) {
    if (event.origin !== window.location.origin) return;
    if (event.data?.type === 'ct-document-request-status') {
      this.hasUnfinishedUpload = !!event.data.hasUnfinishedUpload;
      return;
    }
    if (event.data?.type === 'ct-signature-modal-state') {
      this.signingInProgress = !!event.data.open;
      return;
    }
  }

  async Close() {
    if (!this.hasUnfinishedUpload) {
      this.modalController.dismiss();
      return;
    }

    const alert = await this.alertController.create({
      header: 'Task Not Marked Complete',
      message: "Files have been uploaded here but this hasn't been marked as complete yet. If you are finished, please click 'Mark as Complete' before leaving this page.",
      backdropDismiss: false,
      buttons: [
        { text: 'Keep Working', role: 'cancel' },
        {
          text: 'Mark as Complete and Close',
          handler: () => this.MarkCompleteAndClose()
        },
        {
          text: 'Close without marking as complete',
          role: 'destructive',
          handler: () => this.modalController.dismiss()
        }
      ]
    });
    await alert.present();
  }

  // The "mark complete" action only exists inside the embedded /email iframe's own Angular app -
  // we can't call it directly, so we ask via postMessage (see email.page.ts,
  // onMessageFromParent/MarkCompleteFromParentRequest) and wait for a result before closing, so we
  // don't close out from under a request that actually failed to save.
  private async MarkCompleteAndClose() {
    const iframeWindow = this.emailFrameRef?.nativeElement?.contentWindow;
    if (!iframeWindow) {
      this.globalService.presentToastMessage('Unable to reach the page to mark it complete. Please try again.', false);
      return;
    }

    await this.globalService.presentLoading('Marking as complete...', 999999);
    const success = await this.RequestMarkComplete(iframeWindow);
    await this.globalService.dismissLoading();

    if (success) {
      this.modalController.dismiss();
    } else {
      this.globalService.presentToastMessage('Unable to mark this as complete. Please try again from inside the page.', false);
    }
  }

  private RequestMarkComplete(iframeWindow: Window): Promise<boolean> {
    return new Promise((resolve) => {
      const timeoutId = setTimeout(() => {
        window.removeEventListener('message', listener);
        resolve(false);
      }, 15000);

      const listener = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;
        if (event.data?.type !== 'ct-mark-document-request-complete-result') return;
        clearTimeout(timeoutId);
        window.removeEventListener('message', listener);
        resolve(!!event.data.success);
      };
      window.addEventListener('message', listener);

      iframeWindow.postMessage({ type: 'ct-mark-document-request-complete' }, window.location.origin);
    });
  }

}
